import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsConstants from "../../common/AccountsConstants";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import { formatDate, formatINR, monthStartInput, todayInput } from "../accounts/accountUser";
import LedgerCombo from "../accounts/LedgerCombo";
import PageShell from "../accounts/PageShell";
import useAccountMeta from "../accounts/useAccountMeta";
import { CompanySelect, useCompanyFilter, withCompany } from "../../Components/AccountsFlow/CompanyFilter";
import useDocNav, { DocChip } from "./useDocNav";
import "../accounts/accounts.css";
import "./flow.css";

/* Tally-style ledger statement in entry order, with the customer's credit position and entries waiting for approval. */

export const TYPE_LABEL = { RECEIPT: "Receipt", PAYMENT: "Payment", SALES: "Sales", PURCHASE: "Purchase", JOURNAL: "Journal", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note", CONTRA: "Contra" };

const moneyOrBlank = (amount) => (Number(amount) ? formatINR(amount) : "");
const withAc = (name) => (String(name || "").trim().toLowerCase().endsWith("a/c") ? name : `${name} A/c`);

function balanceText(balance) {
  if (!balance) return "";
  const amount = balance.amount !== undefined ? balance.amount : balance;
  if (!Number(amount)) return formatINR(0);
  return `${formatINR(amount)} ${(balance.type || balance.balanceType) === "CR" ? "Cr" : "Dr"}`;
}

const GROUP_LABEL = { BANK: "bank", CASH: "cash", INCOME: "income", EXPENSE: "expense", LIABILITY: "liability", ASSET: "asset", CAPITAL: "capital", SUNDRY_CREDITOR: "supplier", SUNDRY_DEBTOR: "customer" };

/* Document chips for a row's source (invoice / loading advice / order / purchase bill). */
export function SourceChips({ details }) {
  const nav = useDocNav();
  if (!details) return null;
  const chips = [
    details.invoiceNr && <DocChip key="i" label="Invoice" no={details.invoiceNr} onClick={() => nav.openInvoice(details.invoiceNr)} />,
    details.loadingAdviceNr && <DocChip key="l" label="Loading" no={details.loadingAdviceNr} onClick={() => nav.openLoadingAdvice(details.loadingAdviceNr)} />,
    details.orderNo && <DocChip key="o" label="Order" no={details.orderNo} onClick={() => nav.openOrder(details.orderNo)} />,
    details.documentNo && <DocChip key="p" label="Purchase" no={details.documentNo} />,
  ].filter(Boolean);
  return chips.length ? <span className="flow-doc-links">{chips}</span> : null;
}

export function hasDetails(row) {
  const d = row.details || {};
  return Boolean((d.items || []).length || (d.allocations || []).length || d.paymentMode || d.requestNo || d.invoiceNr || d.documentNo || row.referenceNo);
}

/* Information under a statement row: not part of Dr / Cr or the running balance. */
export function RowDetails({ row }) {
  const d = row.details || {};
  const items = d.items || [];
  const facts = [
    d.paymentMode && `Mode ${d.paymentMode}`,
    row.referenceNo && `Ref ${row.referenceNo}`,
    d.requestNo && `Entered as ${d.requestNo}`,
    d.companyCode && `Company ${d.companyCode}`,
  ].filter(Boolean);
  return (
    <div className="tally-detail-body">
      {(facts.length > 0 || d.invoiceNr || d.documentNo) && (
        <div className="tally-detail-line">
          {facts.length > 0 && <span>{facts.join(" · ")}</span>}
          <SourceChips details={d} />
        </div>
      )}
      {(d.allocations || []).length > 0 && (
        <ul className="tally-detail-items">
          {d.allocations.map((line) => (
            <li key={line.invoiceNr}>
              <span>{line.invoiceNr}</span>
              <span>{formatINR(line.amount)}</span>
            </li>
          ))}
          {Number(d.advance) > 0 ? <li><span>Advance on customer</span><span>{formatINR(d.advance)}</span></li> : null}
          {Number(d.advanceUsed) > 0 ? <li><span>Taken from advance</span><span>{formatINR(d.advanceUsed)}</span></li> : null}
        </ul>
      )}
      {items.length > 0 && (
        <ul className="tally-detail-items">
          {items.map((item, index) => (
            <li key={index}>
              <span>{item.description}{item.quantity ? ` · ${item.quantity} ${item.unit || ""} × ${formatINR(item.rate)}` : ""}</span>
              <span>
                {item.taxable ? `Taxable ${formatINR(item.taxable)}` : ""}
                {item.cgst ? ` · CGST ${formatINR(item.cgst)}` : ""}
                {item.sgst ? ` · SGST ${formatINR(item.sgst)}` : ""}
                {item.igst ? ` · IGST ${formatINR(item.igst)}` : ""}
                {item.gstPercent ? ` (${item.gstPercent}%)` : ""}
                {` = ${formatINR(item.amount)}`}
              </span>
            </li>
          ))}
          {d.roundOff ? <li><span>Round off</span><span>{formatINR(d.roundOff)}</span></li> : null}
        </ul>
      )}
    </div>
  );
}

function Particulars({ ledgerName, row }) {
  const own = withAc(ledgerName || "Ledger");
  const other = withAc(row.particulars || "—");
  const debit = Number(row.debit) > 0;
  return (
    <div className="tally-entry">
      <div className="tally-entry-dr"><span>{debit ? own : other}</span><span>Dr</span></div>
      <div className="tally-entry-to">To {debit ? other : own}</div>
      {row.narration ? <div className="tally-entry-narr">{row.narration}</div> : null}
    </div>
  );
}

export default function FlowLedgerStatement() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { meta } = useAccountMeta();
  const [ledgers, setLedgers] = useState([]);
  const [company, setCompany] = useCompanyFilter();
  const [form, setForm] = useState({ ledgerId: params.get("ledgerId") || "", fromDate: monthStartInput(), toDate: todayInput() });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const rangeError = form.fromDate && form.toDate && form.fromDate > form.toDate ? "To date must be on or after from date" : "";

  useEffect(() => {
    accountsApi.ledgers().then((result) => setLedgers(result?.data || [])).catch((err) => setError(err.message));
  }, []);

  const show = async (event) => {
    if (event) event.preventDefault();
    if (!form.ledgerId || rangeError) return;
    setError("");
    setLoading(true);
    try {
      setData((await accountsFlowApi.statement(withCompany(form, company))).data);
    } catch (err) {
      setData(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const credit = data?.credit;
  return (
    <PageShell title="Ledger Statement" subtitle="Tally style · balances from posted vouchers, in the order they were entered">
      <form className="accounts-panel tally-filter" onSubmit={show}>
        <LedgerCombo label="Ledger" required groups={meta?.ledgerGroups || []}
          ledgers={meta?.ownCustomersOnly ? ledgers.filter((row) => row.group === "SUNDRY_DEBTOR") : ledgers}
          placeholder={meta?.ownCustomersOnly ? "Your customers" : "Search ledger"}
          value={form.ledgerId}
          onChange={(id) => setForm((current) => ({ ...current, ledgerId: id }))} />
        <label className="tally-field">From date
          <input className="form-control" type="date" value={form.fromDate} onChange={(event) => setForm((current) => ({ ...current, fromDate: event.target.value }))} />
        </label>
        <label className="tally-field">To date
          <input className={`form-control${rangeError ? " is-invalid" : ""}`} type="date" value={form.toDate} onChange={(event) => setForm((current) => ({ ...current, toDate: event.target.value }))} />
          {rangeError && <span className="accounts-hint tally-error">{rangeError}</span>}
        </label>
        <CompanySelect value={company} onChange={setCompany} />
        <button className="btn tally-btn" type="submit" disabled={!form.ledgerId || Boolean(rangeError) || loading}>{loading ? "Showing…" : "Show"}</button>
      </form>
      <p className="accounts-hint flow-page-hint">
        Pick any ledger to see every posted voucher on it, oldest first, with the running balance. Dr / Cr as in Tally: a customer's Dr balance is what they owe us, a supplier's Cr balance is what we owe, a cash or bank Dr balance is money in hand.
        For a customer ledger the credit limit, outstanding and available credit are shown as well.
      </p>
      {error && <div className="accounts-error">{error}</div>}
      {data && (
        <>
          <div className="accounts-page-head">
            <div>
              <h3>{data.ledger?.name}</h3>
              <p className="accounts-hint">{data.fromDate ? formatDate(data.fromDate) : "Beginning"} to {data.toDate ? formatDate(data.toDate) : "date"}</p>
            </div>
            <label className="accounts-check-inline flow-details-toggle">
              <input type="checkbox" checked={showDetails} onChange={(event) => setShowDetails(event.target.checked)} />
              <span>Show details</span>
            </label>
          </div>
          {!credit && (
            <div className="accounts-note">
              {data.ledger?.kind === "SUPPLIER"
                ? `${data.ledger.name} is a supplier ledger: a Cr balance is what we still have to pay. Credit limit applies only to customer ledgers.`
                : `Credit limit applies only to customer ledgers. ${data.ledger?.name} is a ${GROUP_LABEL[data.ledger?.group] || "general"} ledger, so only its entries and balance are shown.`}
            </div>
          )}
          {credit && (() => {
            // One picture of the customer's credit: the same figures Order Booking checks.
            const order = credit.orderCheck;
            const limit = order && order.limitSet ? order.creditLimit : credit.creditLimit;
            const outstanding = credit.outstanding;
            const pendingOrders = order ? order.pendingOrders : 0;
            const accountsOrders = credit.openOrders || 0;
            const available = Math.round((limit - outstanding - pendingOrders - accountsOrders) * 100) / 100;
            return (
              <div className="accounts-panel">
                <h3>Credit position{credit.customerCode ? ` · ${credit.customerCode}` : ""}</h3>
                <div className="accounts-facts">
                  <div><span className="accounts-hint">Credit limit</span><strong>{formatINR(limit)}</strong>{credit.creditDays ? <span className="accounts-hint">{credit.creditDays} credit days</span> : null}</div>
                  <div><span className="accounts-hint">Outstanding (this ledger, today)</span><strong>{formatINR(outstanding)}</strong><span className="accounts-hint">invoices − receipts ± notes, all companies</span></div>
                  <div><span className="accounts-hint">Pending orders</span><strong>{formatINR(pendingOrders + accountsOrders)}</strong><span className="accounts-hint">booked, not yet invoiced</span></div>
                  <div><span className="accounts-hint">Available for new orders</span><strong className={available < 0 ? "tally-error" : ""}>{formatINR(available)}</strong><span className="accounts-hint">limit − outstanding − pending orders</span></div>
                </div>
                <div className={`accounts-note ${available < 0 ? "warn" : "ok"}`}>
                  {available < 0
                    ? `Over the limit by ${formatINR(Math.abs(available))}: new orders are blocked until receipts come in or pending orders are invoiced (or the limit is extended).`
                    : `New orders up to ${formatINR(available)} can be booked. Approved receipts and credit notes raise this; invoices and debit notes lower it.`}
                </div>
              </div>
            );
          })()}
          {data.pending?.count > 0 && (
            <div className="accounts-note warn">
              {data.pending.count} {data.pending.count > 1 ? "entries are" : "entry is"} waiting for approval
              {data.pending.credit ? ` · ${formatINR(data.pending.credit)} Cr` : ""}{data.pending.debit ? ` · ${formatINR(data.pending.debit)} Dr` : ""} (not in the balance below).
              {" "}<button type="button" className="btn btn-link btn-sm p-0" onClick={() => navigate(AccountsFlowConstants.VOUCHERS)}>Open receipts &amp; payments</button>
            </div>
          )}
          <div className="accounts-table-wrap">
            <table className="accounts-table tally-statement">
              <thead>
                <tr><th>Date</th><th>Particulars</th><th>Type</th><th>Voucher No</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr>
              </thead>
              <tbody>
                <tr><td /><td colSpan={5}><strong>Opening Balance</strong></td><td className="num"><strong>{balanceText(data.openingBalance)}</strong></td></tr>
                {(data.rows || []).flatMap((row, index) => [
                  <tr key={`${row.voucherId}-${index}`}>
                    <td>{formatDate(row.date)}</td>
                    <td><Particulars ledgerName={data.ledger?.name} row={row} /></td>
                    <td>{TYPE_LABEL[row.voucherType] || row.voucherType}</td>
                    <td>
                      <button type="button" className="accounts-voucher-link" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${row.voucherId}`)}>
                        <span>{row.voucherNo}</span>
                      </button>
                    </td>
                    <td className="num">{moneyOrBlank(row.debit)}</td>
                    <td className="num">{moneyOrBlank(row.credit)}</td>
                    <td className="num">{balanceText({ amount: row.balance, type: row.balanceType })}</td>
                  </tr>,
                  ...(showDetails && hasDetails(row) ? [
                    <tr key={`${row.voucherId}-${index}-details`} className="tally-detail">
                      <td />
                      <td colSpan={6}><RowDetails row={row} /></td>
                    </tr>,
                  ] : []),
                ])}
                {showDetails && (data.pending?.entries || []).length > 0 && (
                  <tr className="tally-pending-head"><td /><td colSpan={6}>Waiting for approval · shown for information, not in the balance</td></tr>
                )}
                {showDetails && (data.pending?.entries || []).map((entry) => (
                  <tr key={`pending-${entry.id}`} className="tally-pending">
                    <td>{formatDate(entry.date)}</td>
                    <td>
                      <div>{entry.requestNo} · {TYPE_LABEL[entry.voucherType] || entry.voucherType}{entry.paymentMode ? ` · ${entry.paymentMode}` : ""}{entry.submittedByName ? ` · by ${entry.submittedByName}` : ""}</div>
                      <SourceChips details={entry.source} />
                      {(entry.allocations || []).length > 0 && <div className="accounts-hint">{entry.allocations.map((line) => `${line.invoiceNr} ${formatINR(line.amount)}`).join(" · ")}</div>}
                    </td>
                    <td>Pending</td>
                    <td>
                      <button type="button" className="accounts-voucher-link" onClick={() => navigate(`${AccountsFlowConstants.ENTRY_VIEW}?id=${entry.id}`)}><span>{entry.requestNo}</span></button>
                    </td>
                    <td className="num">{entry.debit ? `(${formatINR(entry.debit)})` : ""}</td>
                    <td className="num">{entry.credit ? `(${formatINR(entry.credit)})` : ""}</td>
                    <td className="num">not in balance</td>
                  </tr>
                ))}
                {(data.rows || []).length === 0 && <tr><td colSpan={7}>No posted vouchers for this ledger in the period.</td></tr>}
              </tbody>
              <tfoot>
                <tr><td /><td colSpan={3}><strong>Period Total</strong></td><td className="num"><strong>{formatINR(data.totals?.debit)}</strong></td><td className="num"><strong>{formatINR(data.totals?.credit)}</strong></td><td /></tr>
                <tr><td /><td colSpan={5}><strong>Closing Balance</strong></td><td className="num"><strong>{balanceText(data.closingBalance)}</strong></td></tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </PageShell>
  );
}
